import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Statistic, Button, Tag, Spin,
  Typography, Divider, InputNumber, Space, Alert,
  Switch, message
} from 'antd';
import {
  UserOutlined, DollarOutlined, CalendarOutlined,
  CheckCircleOutlined, CreditCardOutlined, ReloadOutlined
} from '@ant-design/icons';
import apiService from 'services/api/apiService';

const { Title, Text } = Typography;

const API = 'https://nexus-hr.talenthubupskilling.com/backend/hr-api/hr-subscription.php';
const tid = () => apiService.getTenantId();

interface Props { data: any; onRefresh: () => void; }

const SubscriptionBilling: React.FC<Props> = ({ onRefresh }) => {
  const [loading, setLoading]         = useState(false);
  const [status, setStatus]           = useState<any>(null);
  const [calcResult, setCalcResult]   = useState<any>(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [empInput, setEmpInput]       = useState<number>(1);
  const [annual, setAnnual]           = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}?action=status&tenant_id=${tid()}`);
      const d = await res.json();
      if (d.success) {
        setStatus(d.data);
        setEmpInput(d.data.employee_count || 1);
      }
    } catch (e) {
      message.error('Failed to load subscription status');
    } finally {
      setLoading(false);
    }
  };

  const calculateCost = async () => {
    setCalcLoading(true);
    try {
      const res = await fetch(`${API}?tenant_id=${tid()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'calculate_cost', employee_count: empInput })
      });
      const d = await res.json();
      if (d.success) setCalcResult(d.data);
    } catch (e) {
      message.error('Failed to calculate cost');
    } finally {
      setCalcLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const sym = status?.currency === 'USD' ? '$' : '₦';
  const features = status?.features || {};

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>Subscription & Billing</Title>
        <Button icon={<ReloadOutlined />} onClick={fetchStatus}>Refresh</Button>
      </div>

      <Spin spinning={loading}>
        {/* Current Status */}
        <Card title="Current Plan" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Statistic
                title="Plan"
                value={status?.plan?.toUpperCase() || 'TRIAL'}
                prefix={<CreditCardOutlined />}
                valueStyle={{ color: status?.plan === 'trial' ? '#faad14' : '#52c41a', fontSize: 20 }}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="Active Employees"
                value={status?.employee_count ?? 0}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#1890ff', fontSize: 20 }}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title="Monthly Cost"
                value={status ? `${sym}${Number(status.monthly_cost || 0).toLocaleString()}` : '—'}
                prefix={<DollarOutlined />}
                valueStyle={{ color: '#52c41a', fontSize: 20 }}
              />
            </Col>
          </Row>

          <Divider />

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Text type="secondary">Price per Employee</Text>
              <div style={{ fontWeight: 600, fontSize: 16 }}>
                {status ? `${sym}${Number(status.price_per_employee || 0).toLocaleString()}/month` : '—'}
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <Text type="secondary">Status</Text>
              <div>
                <Tag color={status?.status === 'active' ? 'green' : status?.status === 'trial' ? 'orange' : 'red'}>
                  {(status?.status || 'trial').toUpperCase()}
                </Tag>
                {status?.is_trial && <Tag color="blue">TRIAL</Tag>}
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <Text type="secondary">Member Since</Text>
              <div style={{ fontWeight: 600 }}>
                {status?.member_since ? new Date(status.member_since).toLocaleDateString() : '—'}
              </div>
            </Col>
          </Row>

          {status?.is_trial && status?.trial_end_date && (
            <Alert
              style={{ marginTop: 16 }}
              type="warning"
              showIcon
              message={`Trial ends: ${new Date(status.trial_end_date).toLocaleDateString()}`}
              description="Upgrade to a paid plan to continue using all features after your trial."
            />
          )}

          {status?.active_subscription && (
            <Alert
              style={{ marginTop: 16 }}
              type="success"
              showIcon
              message="Active subscription"
              description={`Valid until ${new Date(status.active_subscription.end_date).toLocaleDateString()} — ${status.active_subscription.employee_count} employees`}
            />
          )}
        </Card>

        {/* Features */}
        {Object.keys(features).length > 0 && (
          <Card title="Included Features" style={{ marginBottom: 16 }}>
            <Row gutter={[8, 8]}>
              {Object.entries(features).map(([key, val]: [string, any]) => (
                <Col xs={12} sm={8} key={key}>
                  <Space>
                    <CheckCircleOutlined style={{ color: val ? '#52c41a' : '#d9d9d9' }} />
                    <Text style={{ color: val ? '#000' : '#999' }}>
                      {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </Text>
                  </Space>
                </Col>
              ))}
            </Row>
          </Card>
        )}

        {/* Pricing Calculator */}
        <Card title="Pricing Calculator">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={8}>
              <Text strong>Number of Employees</Text>
              <div style={{ marginTop: 8 }}>
                <InputNumber
                  min={1} max={10000}
                  value={empInput}
                  onChange={v => setEmpInput(v || 1)}
                  style={{ width: '100%' }}
                />
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <Text strong>Billing Cycle</Text>
              <div style={{ marginTop: 8 }}>
                <Space>
                  <Text>Monthly</Text>
                  <Switch checked={annual} onChange={setAnnual} />
                  <Text>Annual <Tag color="green">10% off</Tag></Text>
                </Space>
              </div>
            </Col>
            <Col xs={24} sm={8} style={{ paddingTop: 24 }}>
              <Button type="primary" onClick={calculateCost} loading={calcLoading} block>
                Calculate Cost
              </Button>
            </Col>
          </Row>

          {calcResult && (
            <>
              <Divider />
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={8}>
                  <Statistic
                    title="Price per Employee"
                    value={`${calcResult.currency_symbol}${Number(calcResult.price_per_employee).toLocaleString()}`}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col xs={24} sm={8}>
                  <Statistic
                    title={annual ? 'Annual Total' : 'Monthly Total'}
                    value={`${calcResult.currency_symbol}${Number(
                      annual ? calcResult.total_annual_cost : calcResult.total_monthly_cost
                    ).toLocaleString()}`}
                    valueStyle={{ color: '#52c41a', fontWeight: 700 }}
                  />
                </Col>
                {annual && (
                  <Col xs={24} sm={8}>
                    <Statistic
                      title="Annual Saving"
                      value={`${calcResult.currency_symbol}${Number(
                        calcResult.total_monthly_cost * 12 - calcResult.total_annual_cost
                      ).toLocaleString()}`}
                      valueStyle={{ color: '#faad14' }}
                    />
                  </Col>
                )}
              </Row>
              <Alert
                style={{ marginTop: 16 }}
                type="info"
                message={`Based on ${calcResult.employee_count} employee${calcResult.employee_count !== 1 ? 's' : ''} at ${calcResult.currency_symbol}${Number(calcResult.price_per_employee).toLocaleString()} per employee per month`}
              />
            </>
          )}
        </Card>
      </Spin>
    </div>
  );
};

export default SubscriptionBilling;
